import React from "react";

import { ExternalLink } from "lucide-react";

import type {
  XFactorCitation,
  XFactorScoreWithExplanation,
} from "../../../Types/XFactorBriefing";

/**
 * Coerce a model-authored field to renderable text.
 *
 * xfactor publishes no schema, and fields that are strings in one place are
 * objects in another — `overallHealthScoreRationale`, for instance, is
 * `{reasoning, drivers, evidence}`, not the string the name implies. Passing
 * such a value to JSX throws "Objects are not valid as a React child" and takes
 * the whole page down. Everything model-authored goes through here.
 */
export const toText = (value: unknown): string | null => {
  if (value == null) return null;
  if (typeof value === "string") return value.trim() || null;
  if (typeof value === "number" || typeof value === "boolean")
    return String(value);
  if (Array.isArray(value)) {
    const parts = value.map(toText).filter(Boolean) as string[];
    return parts.length ? parts.join(" · ") : null;
  }
  if (typeof value === "object") {
    const o = value as Record<string, unknown>;
    // Prefer the conventional prose keys before falling back.
    for (const k of [
      "reasoning",
      "text",
      "synopsis",
      "explanation",
      "summary",
      "value",
      "description",
    ]) {
      const t = toText(o[k]);
      if (t) return t;
    }
    return null;
  }
  return null;
};

/**
 * Coerce a score field to a number.
 *
 * Same hazard as `toText`: `sectionHealthScore` is `{value, explanation}` on
 * sections but a bare number on the summary. `Math.round({...})` yields NaN and
 * React then renders the literal string "NaN".
 */
export const toScore = (value: unknown): number | null => {
  if (value == null) return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value === "string") {
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  }
  if (typeof value === "object") {
    const o = value as Record<string, unknown>;
    for (const k of ["value", "score", "sectionScore"]) {
      const n = toScore(o[k]);
      if (n != null) return n;
    }
  }
  return null;
};

/** The prose that sometimes rides along with a score object. */
export const toScoreExplanation = (value: unknown): string | null => {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    return toText((value as Record<string, unknown>).explanation);
  }
  return null;
};

/** Supporting bullets on a rationale object, when present. */
export const toDrivers = (value: unknown): string[] => {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    const d = (value as Record<string, unknown>).drivers;
    if (Array.isArray(d)) return d.map(toText).filter(Boolean) as string[];
  }
  return [];
};

/** 0–100 → colour band. Mirrors the R/Y/G convention used elsewhere in EB. */
export const scoreTone = (score?: number | null) => {
  if (score == null || Number.isNaN(score))
    return { text: "text-gray-400", bg: "bg-gray-100", ring: "ring-gray-200" };
  if (score >= 80)
    return {
      text: "text-green-700",
      bg: "bg-green-50",
      ring: "ring-green-200",
    };
  if (score >= 50)
    return {
      text: "text-amber-700",
      bg: "bg-amber-50",
      ring: "ring-amber-200",
    };
  return { text: "text-red-700", bg: "bg-red-50", ring: "ring-red-200" };
};

/**
 * Risk is inverted: a HIGH risk score is bad, unlike health/opportunity where
 * high is good. Rendering it on the shared scale would colour a severe risk
 * green.
 */
export const riskTone = (score?: number | null) => {
  if (score == null) return scoreTone(null);
  return scoreTone(100 - score);
};

export const priorityTone = (p?: string) => {
  switch ((p || "").toLowerCase()) {
    case "high":
    case "critical":
      return "bg-red-50 text-red-700 ring-red-200";
    case "medium":
      return "bg-amber-50 text-amber-700 ring-amber-200";
    case "low":
      return "bg-green-50 text-green-700 ring-green-200";
    default:
      return "bg-gray-50 text-gray-600 ring-gray-200";
  }
};

export const Pill: React.FC<{
  className?: string;
  /** Native tooltip — used to surface a score's explanation on hover. */
  title?: string;
  children: React.ReactNode;
}> = ({ className = "", title, children }) => (
  <span
    title={title}
    className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${className}`}
  >
    {children}
  </span>
);

/**
 * A score tile. `rationale` is rendered inline rather than in a tooltip — the
 * model's reasoning is the useful part and hiding it behind hover loses it on
 * touch devices.
 */
export const ScoreTile: React.FC<{
  label: string;
  score?: number | null;
  /** May be a string or a {reasoning, drivers, evidence} object. */
  rationale?: unknown;
  inverted?: boolean;
}> = ({ label, score, rationale, inverted = false }) => {
  const tone = inverted ? riskTone(score) : scoreTone(score);
  const missing = score == null;
  const reasoning = toText(rationale);
  const drivers = toDrivers(rationale);
  const [open, setOpen] = React.useState(false);

  return (
    <div className={`rounded-lg p-3 ring-1 ring-inset ${tone.bg} ${tone.ring}`}>
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-xs font-medium uppercase tracking-wide text-gray-600">
          {label}
        </span>
        <span className={`text-lg font-semibold ${tone.text}`}>
          {missing ? "—" : Math.round(score as number)}
        </span>
      </div>
      {missing ? (
        <p className="mt-1 text-xs italic text-gray-400">Not scored</p>
      ) : reasoning ? (
        <>
          <p className="mt-1 text-xs leading-snug text-gray-600">{reasoning}</p>
          {drivers.length ? (
            <>
              <button
                type="button"
                onClick={() => setOpen(o => !o)}
                className="mt-1 text-[11px] font-medium text-gray-500 underline-offset-2 hover:underline"
              >
                {open ? "Hide" : `${drivers.length} contributing factors`}
              </button>
              {open ? (
                <ul className="mt-1 space-y-0.5">
                  {drivers.map((d, i) => (
                    <li
                      key={i}
                      className="text-[11px] leading-snug text-gray-600"
                    >
                      {d}
                    </li>
                  ))}
                </ul>
              ) : null}
            </>
          ) : null}
        </>
      ) : null}
    </div>
  );
};

export const RelevanceNote: React.FC<{
  relevance?: XFactorScoreWithExplanation | number;
}> = ({ relevance }) => {
  const value = toScore(
    typeof relevance === "object" && relevance !== null
      ? relevance.value
      : relevance
  );
  if (value == null) return null;
  const tone = scoreTone(value);
  return (
    <div className="mt-2 flex items-start gap-2">
      <Pill className={`${tone.bg} ${tone.text} ${tone.ring} shrink-0`}>
        Relevance {Math.round(value)}
      </Pill>
      {toText(
        typeof relevance === "object" && relevance !== null
          ? relevance.explanation
          : null
      ) ? (
        <p className="text-xs leading-snug text-gray-600">
          {toText(
            typeof relevance === "object" && relevance !== null
              ? relevance.explanation
              : null
          )}
        </p>
      ) : null}
    </div>
  );
};

/**
 * Renders `evidence_refs` (e.g. ["c1","c3"]) as numbered chips linking to the
 * matching citation. Refs with no matching citation are shown greyed rather
 * than dropped — a dangling reference is information, not noise.
 */
export const CitationRefs: React.FC<{
  refs?: string[];
  citations?: XFactorCitation[];
}> = ({ refs, citations }) => {
  if (!refs?.length) return null;
  const byId = new Map((citations || []).map(c => [c.id, c]));

  return (
    <div className="mt-1.5 flex flex-wrap items-center gap-1">
      {refs.map(ref => {
        const c = byId.get(ref);
        if (!c?.url) {
          return (
            <span
              key={ref}
              title="Referenced source not present in this section's citations"
              className="inline-flex items-center rounded border border-dashed border-gray-300 px-1.5 py-0.5 text-[11px] text-gray-400"
            >
              {ref}
            </span>
          );
        }
        return (
          <a
            key={ref}
            href={c.url}
            target="_blank"
            rel="noopener noreferrer"
            title={[c.title, c.source, c.published_at]
              .filter(Boolean)
              .join(" · ")}
            className="inline-flex items-center gap-1 rounded border border-gray-200 bg-gray-50 px-1.5 py-0.5 text-[11px] text-gray-700 transition-colors hover:border-[#974ae4] hover:text-[#974ae4]"
          >
            {c.source || ref}
            <ExternalLink size={9} aria-hidden />
          </a>
        );
      })}
    </div>
  );
};

export const SourceList: React.FC<{ citations?: XFactorCitation[] }> = ({
  citations,
}) => {
  if (!citations?.length) return null;
  return (
    <ol className="space-y-1.5">
      {citations.map(c => (
        <li key={c.id} className="text-xs leading-snug">
          <span className="mr-1.5 font-mono text-[10px] text-gray-400">
            {c.id}
          </span>
          {c.url ? (
            <a
              href={c.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#974ae4] hover:underline"
            >
              {c.title || c.url}
            </a>
          ) : (
            <span className="text-gray-700">
              {c.title || "Untitled source"}
            </span>
          )}
          {(c.source || c.published_at) && (
            <span className="ml-1 text-gray-400">
              — {[c.source, c.published_at].filter(Boolean).join(", ")}
            </span>
          )}
        </li>
      ))}
    </ol>
  );
};

export const EmptyNote: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => <p className="text-xs italic text-gray-400">{children}</p>;
